import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Building2, 
  Calendar, 
  Save, 
  CheckCircle2,
  Plus,
  RefreshCw,
  Receipt,
  FileText,
  Zap,
  ArrowRight,
  Circle,
  Sparkles,
  Shield,
  FileSpreadsheet,
  BookOpen,
  ChevronRight,
  Hash,
  MapPin,
  Phone,
  Mail,
  Building,
  Edit,
  Settings,
  Globe,
  Landmark,
  Trash2,
  Loader2
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { api, getErrorMessage } from '../App';
import { useUser } from '../utils/userHelper';

const FinanceSetup = ({ user: propUser, selectedCompany }) => {
  const navigate = useNavigate();
  // Use the useUser hook to get user from prop or localStorage
  const user = useUser(propUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stateCodes, setStateCodes] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [settings, setSettings] = useState(null);
  const [coaInitialized, setCoaInitialized] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);
  
  // Bank management states
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [savingBank, setSavingBank] = useState(false);
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_name: '',
    account_number: '',
    ifsc_code: '',
    branch: '',
    opening_balance: 0
  });
  
  // Setup wizard states
  const [setupMode, setSetupMode] = useState(null); // 'quick' or 'full'
  const [setupStep, setSetupStep] = useState(1);
  
  // Finance Admin can only view company details, not edit them
  const isFinanceAdmin = user?.role === 'finance_admin';
  const canEditCompanyDetails = user?.role === 'company_admin' || user?.role === 'super_admin' || user?.role === 'hotel_admin';
  
  const [formData, setFormData] = useState({
    legal_name: '',
    trade_name: '',
    pan_number: '',
    gstin: '',
    gst_type: 'regular',
    state_code: '',
    registered_address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    website: ''
  });

  const [fyForm, setFyForm] = useState({
    name: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    // Fetch data if we have company context OR if user is hotel_admin
    if (selectedCompany || user?.company_id || user?.role === 'hotel_admin') {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [selectedCompany, user?.company_id, user?.role]);

  // Fetch banks when setup is complete and COA is initialized
  useEffect(() => {
    if (coaInitialized) {
      fetchBanks();
    }
  }, [coaInitialized]);

  const getCompanyId = () => {
    return selectedCompany?.id || user?.company_id;
  };

  const fetchData = async () => {
    // For hotel_admin, we don't need company_id - the backend uses hotel context
    // Only require companyId for company_admin or super_admin
    const companyId = getCompanyId();
    const isHotelAdmin = user?.role === 'hotel_admin';
    
    if (!companyId && !isHotelAdmin) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      if (selectedCompany) {
        setCompanyInfo(selectedCompany);
      } else if (user?.company_id) {
        const companyRes = await api.get(`/companies/${user.company_id}`).catch(() => ({ data: null }));
        setCompanyInfo(companyRes.data);
      } else if (isHotelAdmin && user?.hotel_id) {
        // Get hotel info for hotel_admin
        try {
          const hotelRes = await api.get('/auth/my-hotel');
          setCompanyInfo({ name: hotelRes.data?.name || 'Your Hotel' });
        } catch (e) {
          setCompanyInfo({ name: 'Your Hotel' });
        }
      }
      
      const [statesRes, settingsRes, fyRes, groupsRes] = await Promise.all([
        api.get('/finance/state-codes').catch(() => ({ data: [] })),
        api.get('/finance/settings').catch(() => ({ data: null })),
        api.get('/finance/financial-years').catch(() => ({ data: [] })),
        api.get('/finance/account-groups').catch(() => ({ data: [] }))
      ]);
      
      setStateCodes(statesRes.data || []);
      setFinancialYears(fyRes.data || []);
      setCoaInitialized((groupsRes.data || []).length > 0);
      
      // Check if setup is complete
      const hasSettings = settingsRes.data?.legal_name;
      const hasFY = (fyRes.data || []).length > 0;
      const hasCOA = (groupsRes.data || []).length > 0;
      setSetupComplete(hasSettings && hasFY && hasCOA);
      
      if (settingsRes.data) {
        setSettings(settingsRes.data);
        setFormData({
          legal_name: settingsRes.data.legal_name || '',
          trade_name: settingsRes.data.trade_name || '',
          pan_number: settingsRes.data.pan_number || '',
          gstin: settingsRes.data.gstin || '',
          gst_type: settingsRes.data.gst_type || 'regular',
          state_code: settingsRes.data.state_code || '',
          registered_address: settingsRes.data.registered_address || '',
          city: settingsRes.data.city || '',
          state: settingsRes.data.state || '',
          pincode: settingsRes.data.pincode || '',
          phone: settingsRes.data.phone || '',
          email: settingsRes.data.email || '',
          website: settingsRes.data.website || ''
        });
      }
    } catch (error) {
      console.error('Error fetching setup data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Bank management functions
  const fetchBanks = async () => {
    setLoadingBanks(true);
    try {
      const response = await api.get('/finance/ledgers');
      // Filter ledgers that are under Cash & Bank group (code 1110) - codes starting with 111
      // Exclude Cash in Hand (1111) as it's not a bank account
      const bankLedgers = response.data.filter(ledger => {
        const code = ledger.code;
        if (!code) return false;
        // Include codes 1112+ (excluding 1111 which is Cash in Hand)
        return code.startsWith('111') && code !== '1111';
      });
      setBanks(bankLedgers);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    } finally {
      setLoadingBanks(false);
    }
  };

  const openBankDialog = (bank = null) => {
    if (bank) {
      setEditingBank(bank);
      setBankForm({
        bank_name: bank.bank_name || bank.name?.split(' - ')[0] || '',
        account_name: bank.account_name || '',
        account_number: bank.account_number || '',
        ifsc_code: bank.ifsc_code || '',
        branch: bank.branch || '',
        opening_balance: bank.opening_balance || 0
      });
    } else {
      setEditingBank(null);
      setBankForm({
        bank_name: '',
        account_name: '',
        account_number: '',
        ifsc_code: '',
        branch: '',
        opening_balance: 0
      });
    }
    setBankDialogOpen(true);
  };

  const handleSaveBank = async () => {
    if (!bankForm.bank_name.trim()) {
      toast.error('Bank name is required');
      return;
    }
    if (!bankForm.account_number.trim()) {
      toast.error('Account number is required');
      return;
    }

    setSavingBank(true);
    try {
      // Get the Cash & Bank group (code 1110)
      const groupsRes = await api.get('/finance/account-groups');
      const bankGroup = groupsRes.data.find(g => 
        g.code === '1110' || 
        g.name?.toLowerCase().includes('cash & bank') ||
        g.name?.toLowerCase() === 'cash and bank'
      );
      
      if (!bankGroup) {
        toast.error('Cash & Bank group not found. Please initialize Finance Setup first.');
        setSavingBank(false);
        return;
      }

      // Get existing ledgers to determine next code
      const ledgersRes = await api.get('/finance/ledgers');
      const existingBankCodes = ledgersRes.data
        .filter(l => l.code?.startsWith('111') && l.account_group_id === bankGroup.id)
        .map(l => parseInt(l.code) || 0);
      const nextCode = existingBankCodes.length > 0 ? Math.max(...existingBankCodes) + 1 : 1111;
      const newCode = String(nextCode);

      const lastFourDigits = bankForm.account_number.slice(-4);
      const ledgerName = `${bankForm.bank_name} - ${lastFourDigits}`;
      
      const ledgerData = {
        name: ledgerName,
        code: editingBank ? editingBank.code : newCode,
        account_group_id: bankGroup.id,
        opening_balance: parseFloat(bankForm.opening_balance) || 0,
        bank_name: bankForm.bank_name,
        account_name: bankForm.account_name,
        account_number: bankForm.account_number,
        ifsc_code: bankForm.ifsc_code,
        branch: bankForm.branch,
        is_active: true
      };

      if (editingBank) {
        await api.put(`/finance/ledgers/${editingBank.id}`, ledgerData);
        toast.success('Bank account updated');
      } else {
        await api.post('/finance/ledgers', ledgerData);
        toast.success('Bank account added');
      }

      setBankDialogOpen(false);
      fetchBanks();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save bank account'));
    } finally {
      setSavingBank(false);
    }
  };

  const handleDeleteBank = async (bank) => {
    if (!window.confirm(`Are you sure you want to delete ${bank.name}?`)) return;
    
    try {
      await api.delete(`/finance/ledgers/${bank.id}`);
      toast.success('Bank account deleted');
      fetchBanks();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete bank account'));
    }
  };

  const handleQuickSetup = async () => {
    try {
      setSaving(true);
      
      // Generate FY name
      const today = new Date();
      let startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      let endYear = startYear + 1;
      
      // Create financial year if none exists
      if (financialYears.length === 0) {
        await api.post('/finance/financial-years', {
          name: `FY ${startYear}-${String(endYear).slice(-2)}`,
          start_date: `${startYear}-04-01`,
          end_date: `${endYear}-03-31`
        }).catch(() => {});
      }
      
      // Initialize Chart of Accounts
      const response = await api.post('/finance/initialize-coa');
      toast.success(`Setup complete! Created ${response.data.account_groups} account groups and ${response.data.ledgers} ledgers.`);
      setSetupComplete(true);
      setSetupMode(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to complete setup'));
    } finally {
      setSaving(false);
    }
  };

  const handleFullSetup = async () => {
    try {
      setSaving(true);
      
      // Save company settings
      if (formData.legal_name) {
        await api.post('/finance/settings', formData);
      }
      
      // Generate FY name
      const today = new Date();
      let startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      let endYear = startYear + 1;
      
      // Create financial year if none exists
      if (financialYears.length === 0) {
        await api.post('/finance/financial-years', {
          name: `FY ${startYear}-${String(endYear).slice(-2)}`,
          start_date: `${startYear}-04-01`,
          end_date: `${endYear}-03-31`
        }).catch(() => {});
      }
      
      // Initialize Chart of Accounts
      const response = await api.post('/finance/initialize-coa');
      toast.success(`Setup complete! Your finance module is ready.`);
      setSetupComplete(true);
      setSetupMode(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to complete setup'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!formData.legal_name) {
      toast.error('Legal name is required');
      return;
    }
    
    try {
      setSaving(true);
      await api.post('/finance/settings', formData);
      toast.success('Settings updated successfully');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFY = async () => {
    if (!fyForm.name || !fyForm.start_date || !fyForm.end_date) {
      toast.error('Please fill all financial year fields');
      return;
    }
    
    try {
      setSaving(true);
      await api.post('/finance/financial-years', fyForm);
      toast.success('Financial year created successfully');
      setFyForm({ name: '', start_date: '', end_date: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create financial year'));
    } finally {
      setSaving(false);
    }
  };

  const generateFYName = () => {
    const today = new Date();
    let startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    let endYear = startYear + 1;
    
    setFyForm({
      name: `FY ${startYear}-${String(endYear).slice(-2)}`,
      start_date: `${startYear}-04-01`,
      end_date: `${endYear}-03-31`
    });
  };

  const setCurrentFY = async (fyId) => {
    try {
      await api.patch(`/finance/financial-years/${fyId}/set-current`);
      toast.success('Current financial year updated');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to set financial year'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Show message if no company selected (not for hotel_admin who works at hotel level)
  const isHotelAdmin = user?.role === 'hotel_admin';
  if (!getCompanyId() && !isHotelAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Company Selected</h3>
            <p className="text-gray-500">Please select a company from the header to configure finance settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // For hotel_admin without company context, show setup directly based on finance data
  const effectiveCompanyInfo = companyInfo || (isHotelAdmin ? { name: 'Your Hotel' } : null);

  // =====================
  // SETUP WIZARD (if not configured)
  // =====================
  if (!setupComplete && !setupMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg mb-4">
              <Settings className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Finance Setup</h1>
            <p className="text-gray-600 max-w-lg mx-auto">
              Configure your accounting system for <span className="font-semibold text-blue-600">{effectiveCompanyInfo?.name || 'your business'}</span>
            </p>
          </div>

          {/* Setup Options */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Quick Setup Card */}
            <Card 
              className="relative overflow-hidden border-2 hover:border-blue-400 transition-all cursor-pointer group"
              onClick={() => setSetupMode('quick')}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full -mr-16 -mt-16 opacity-50" />
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-blue-100 text-blue-600">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Quick Setup</CardTitle>
                    <Badge className="mt-1 bg-blue-100 text-blue-700 hover:bg-blue-100">Recommended</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Get started in seconds with pre-configured settings optimized for the hospitality industry.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>Standard Chart of Accounts</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>GST Rates (0%, 5%, 12%, 18%, 28%)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>HSN/SAC Codes for Hotels</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>Current Financial Year (Apr-Mar)</span>
                  </div>
                </div>
                <div className="pt-2">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 group-hover:shadow-lg transition-all">
                    Start Quick Setup
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Full Setup Card */}
            <Card 
              className="relative overflow-hidden border-2 hover:border-indigo-400 transition-all cursor-pointer group"
              onClick={() => setSetupMode('full')}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-100 to-purple-200 rounded-full -mr-16 -mt-16 opacity-50" />
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-indigo-100 text-indigo-600">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Company Setup</CardTitle>
                    <Badge variant="outline" className="mt-1">Customizable</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Configure your company details, GSTIN, and customize settings for compliance.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Circle className="h-4 w-4 text-indigo-400" />
                    <span>Company Registration Details</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Circle className="h-4 w-4 text-indigo-400" />
                    <span>GSTIN & PAN Configuration</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Circle className="h-4 w-4 text-indigo-400" />
                    <span>Custom Financial Year</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Circle className="h-4 w-4 text-indigo-400" />
                    <span>Advanced Tax Settings</span>
                  </div>
                </div>
                <div className="pt-2">
                  <Button variant="outline" className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50 group-hover:shadow-lg transition-all">
                    Start Company Setup
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Features Preview */}
          <Card className="bg-white/70 backdrop-blur">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                What&apos;s Included
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-green-50 text-center">
                  <BookOpen className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-green-800">Chart of Accounts</p>
                  <p className="text-xs text-green-600">50+ Ledgers</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 text-center">
                  <Receipt className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-blue-800">GST Compliance</p>
                  <p className="text-xs text-blue-600">GSTR-1, 3B Ready</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50 text-center">
                  <FileSpreadsheet className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-purple-800">Financial Reports</p>
                  <p className="text-xs text-purple-600">P&L, Balance Sheet</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 text-center">
                  <Shield className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-amber-800">Audit Trail</p>
                  <p className="text-xs text-amber-600">Complete History</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quick Setup Confirmation
  if (setupMode === 'quick') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-2xl mx-auto">
          <Button 
            variant="ghost" 
            className="mb-4"
            onClick={() => setSetupMode(null)}
          >
            ← Back to options
          </Button>
          
          <Card className="border-2 border-blue-200">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl">Quick Setup</CardTitle>
              <CardDescription className="text-base">
                We&apos;ll set up everything automatically with industry-standard defaults
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  This will create:
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-blue-800">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    15 Account Groups
                  </div>
                  <div className="flex items-center gap-2 text-blue-800">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    50+ Standard Ledgers
                  </div>
                  <div className="flex items-center gap-2 text-blue-800">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    GST Rate Configuration
                  </div>
                  <div className="flex items-center gap-2 text-blue-800">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Financial Year (Apr-Mar)
                  </div>
                  <div className="flex items-center gap-2 text-blue-800">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    HSN/SAC Codes
                  </div>
                  <div className="flex items-center gap-2 text-blue-800">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Tax Ledgers (CGST, SGST, IGST)
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSetupMode(null)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleQuickSetup}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Complete Setup
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Full Company Setup Wizard
  if (setupMode === 'full') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 p-6">
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            className="mb-4"
            onClick={() => setSetupMode(null)}
          >
            ← Back to options
          </Button>

          <div className="mb-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium text-gray-700">Step {setupStep} of 2</span>
              <span className="text-gray-500">{setupStep === 1 ? 'Company Details' : 'Confirm Setup'}</span>
            </div>
            <Progress value={setupStep * 50} className="h-2" />
          </div>

          <Card className="border-2 border-indigo-200">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-indigo-100 text-indigo-600">
                  {setupStep === 1 ? <Building2 className="h-6 w-6" /> : <CheckCircle2 className="h-6 w-6" />}
                </div>
                <div>
                  <CardTitle>{setupStep === 1 ? 'Company Details' : 'Confirm & Complete'}</CardTitle>
                  <CardDescription>
                    {setupStep === 1 ? 'Enter your business registration details' : 'Review and complete setup'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {setupStep === 1 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 md:col-span-1">
                      <Label htmlFor="legal_name">Legal Name *</Label>
                      <Input
                        id="legal_name"
                        placeholder="Company Legal Name"
                        value={formData.legal_name}
                        onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <Label htmlFor="trade_name">Trade Name</Label>
                      <Input
                        id="trade_name"
                        placeholder="Trading As"
                        value={formData.trade_name}
                        onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="gstin" className="flex items-center gap-1">
                        <Hash className="h-3 w-3" /> GSTIN
                      </Label>
                      <Input
                        id="gstin"
                        placeholder="22AAAAA0000A1Z5"
                        maxLength={15}
                        value={formData.gstin}
                        onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="pan_number">PAN</Label>
                      <Input
                        id="pan_number"
                        placeholder="AAAAA0000A"
                        maxLength={10}
                        value={formData.pan_number}
                        onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="registered_address" className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Address
                    </Label>
                    <Input
                      id="registered_address"
                      placeholder="Registered Address"
                      value={formData.registered_address}
                      onChange={(e) => setFormData({ ...formData, registered_address: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        placeholder="City"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        placeholder="State"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="pincode">PIN Code</Label>
                      <Input
                        id="pincode"
                        placeholder="500001"
                        maxLength={6}
                        value={formData.pincode}
                        onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="flex items-center gap-1">
                        <Phone className="h-3 w-3" /> Phone
                      </Label>
                      <Input
                        id="phone"
                        placeholder="+91 9876543210"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="flex items-center gap-1">
                        <Mail className="h-3 w-3" /> Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="accounts@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setSetupMode(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => setSetupStep(2)}
                    >
                      Next Step
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </>
              )}

              {setupStep === 2 && (
                <>
                  <div className="bg-indigo-50 rounded-xl p-4 space-y-4">
                    <h3 className="font-semibold text-indigo-900">Setup Summary</h3>
                    
                    {formData.legal_name && (
                      <div className="flex items-start gap-3 text-sm">
                        <Building className="h-4 w-4 text-indigo-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-indigo-900">{formData.legal_name}</p>
                          {formData.gstin && <p className="text-indigo-700">GSTIN: {formData.gstin}</p>}
                        </div>
                      </div>
                    )}
                    
                    <div className="border-t border-indigo-200 pt-3 space-y-2">
                      <p className="text-sm font-medium text-indigo-900">Will be created:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm text-indigo-800">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          Chart of Accounts
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          GST Configuration
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          Financial Year
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          Standard Ledgers
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setSetupStep(1)}
                      disabled={saving}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      onClick={handleFullSetup}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Setting up...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Complete Setup
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // =====================
  // EDIT/UPDATE MODE (After Setup Complete)
  // =====================
  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finance Settings</h1>
          <p className="text-gray-600">
            Manage accounting settings for <span className="font-semibold text-blue-600">{companyInfo?.name || 'Selected Company'}</span>
          </p>
        </div>
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Setup Complete
        </Badge>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="company" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Company Details
          </TabsTrigger>
          <TabsTrigger value="gst" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            GST Settings
          </TabsTrigger>
          <TabsTrigger value="financial-year" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Financial Year
          </TabsTrigger>
          <TabsTrigger value="banks" className="flex items-center gap-2">
            <Landmark className="h-4 w-4" />
            Bank Accounts
          </TabsTrigger>
        </TabsList>

        {/* Company Details Tab */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </CardTitle>
              <CardDescription>
                Update your company registration and contact details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_legal_name">Legal Name</Label>
                  <Input
                    id="edit_legal_name"
                    value={formData.legal_name}
                    onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_trade_name">Trade Name</Label>
                  <Input
                    id="edit_trade_name"
                    value={formData.trade_name}
                    onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit_address">Address</Label>
                <Input
                  id="edit_address"
                  value={formData.registered_address}
                  onChange={(e) => setFormData({ ...formData, registered_address: e.target.value })}
                  disabled={!canEditCompanyDetails}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
                <div>
                  <Label>PIN Code</Label>
                  <Input
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
              </div>

              {canEditCompanyDetails && (
                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveSettings} disabled={saving}>
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GST Settings Tab */}
        <TabsContent value="gst">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                GST Configuration
              </CardTitle>
              <CardDescription>
                Manage GST registration details for tax compliance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>GSTIN</Label>
                  <Input
                    value={formData.gstin}
                    onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                    maxLength={15}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
                <div>
                  <Label>PAN</Label>
                  <Input
                    value={formData.pan_number}
                    onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                    maxLength={10}
                    disabled={!canEditCompanyDetails}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>GST Type</Label>
                  <Select
                    value={formData.gst_type}
                    onValueChange={(value) => setFormData({ ...formData, gst_type: value })}
                    disabled={!canEditCompanyDetails}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="composition">Composition</SelectItem>
                      <SelectItem value="unregistered">Unregistered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>State Code</Label>
                  <Select
                    value={formData.state_code}
                    onValueChange={(value) => setFormData({ ...formData, state_code: value })}
                    disabled={!canEditCompanyDetails}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                      {stateCodes.map(state => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.code} - {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {canEditCompanyDetails && (
                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveSettings} disabled={saving}>
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Year Tab */}
        <TabsContent value="financial-year">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Financial Years
              </CardTitle>
              <CardDescription>
                Manage financial years for accounting periods
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Existing Financial Years */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Existing Financial Years</Label>
                {financialYears.length > 0 ? (
                  <div className="space-y-2">
                    {financialYears.map(fy => (
                      <div key={fy.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{fy.name}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(fy.start_date).toLocaleDateString()} - {new Date(fy.end_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {fy.is_current ? (
                            <Badge className="bg-green-100 text-green-800">Current</Badge>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => setCurrentFY(fy.id)}>
                              Set as Current
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No financial years configured</p>
                )}
              </div>

              {/* Add New Financial Year */}
              {canEditCompanyDetails && (
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-sm font-medium">Add New Financial Year</Label>
                    <Button variant="outline" size="sm" onClick={generateFYName}>
                      <Plus className="h-4 w-4 mr-1" />
                      Generate Current FY
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        placeholder="FY 2024-25"
                        value={fyForm.name}
                        onChange={(e) => setFyForm({ ...fyForm, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={fyForm.start_date}
                        onChange={(e) => setFyForm({ ...fyForm, start_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={fyForm.end_date}
                        onChange={(e) => setFyForm({ ...fyForm, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-4">
                    <Button onClick={handleCreateFY} disabled={saving}>
                      {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                      Create Financial Year
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Accounts Tab */}
        <TabsContent value="banks">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Landmark className="h-5 w-5" />
                    Bank Accounts
                  </CardTitle>
                  <CardDescription>Manage company bank accounts for financial transactions</CardDescription>
                </div>
                <Button 
                  onClick={() => openBankDialog()}
                  disabled={!coaInitialized}
                  data-testid="add-bank-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Bank Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!coaInitialized ? (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Please complete Finance Setup first</p>
                  <p className="text-sm text-gray-400 mt-1">Initialize Chart of Accounts to manage bank accounts</p>
                </div>
              ) : loadingBanks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                  <span className="ml-2 text-gray-500">Loading bank accounts...</span>
                </div>
              ) : banks.length === 0 ? (
                <div className="text-center py-8">
                  <Landmark className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No bank accounts configured</p>
                  <p className="text-sm text-gray-400 mt-1">Add your first bank account to get started</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bank Name</TableHead>
                      <TableHead>Account Number</TableHead>
                      <TableHead>IFSC Code</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {banks.map((bank) => {
                      const hasTransactions = (bank.current_balance || 0) !== (bank.opening_balance || 0);
                      return (
                        <TableRow key={bank.id} data-testid={`bank-row-${bank.id}`}>
                          <TableCell className="font-medium">{bank.bank_name || bank.name}</TableCell>
                          <TableCell className="font-mono">{bank.account_number || '-'}</TableCell>
                          <TableCell className="font-mono">{bank.ifsc_code || '-'}</TableCell>
                          <TableCell>{bank.branch || '-'}</TableCell>
                          <TableCell className="text-right font-medium">
                            ₹{((bank.current_balance || 0) + (bank.opening_balance || 0)).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openBankDialog(bank)}
                                disabled={hasTransactions}
                                title={hasTransactions ? "Cannot edit: Bank has transactions" : "Edit bank"}
                                data-testid={`edit-bank-${bank.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteBank(bank)}
                                disabled={hasTransactions}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title={hasTransactions ? "Cannot delete: Bank has transactions" : "Delete bank"}
                                data-testid={`delete-bank-${bank.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Bank Dialog */}
      <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              {editingBank ? 'Edit Bank Account' : 'Add Bank Account'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="bank_name">Bank Name *</Label>
              <Input
                id="bank_name"
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                placeholder="e.g., HDFC Bank, ICICI Bank"
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <Label htmlFor="account_name">Account Holder Name</Label>
              <Input
                id="account_name"
                value={bankForm.account_name}
                onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })}
                placeholder="Name on account"
              />
            </div>
            <div>
              <Label htmlFor="account_number">Account Number *</Label>
              <Input
                id="account_number"
                value={bankForm.account_number}
                onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                placeholder="Enter account number"
                className="font-mono"
                data-testid="account-number-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ifsc_code">IFSC Code</Label>
                <Input
                  id="ifsc_code"
                  value={bankForm.ifsc_code}
                  onChange={(e) => setBankForm({ ...bankForm, ifsc_code: e.target.value.toUpperCase() })}
                  placeholder="HDFC0001234"
                  className="font-mono"
                  maxLength={11}
                />
              </div>
              <div>
                <Label htmlFor="branch">Branch</Label>
                <Input
                  id="branch"
                  value={bankForm.branch}
                  onChange={(e) => setBankForm({ ...bankForm, branch: e.target.value })}
                  placeholder="Branch name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="opening_balance">Opening Balance (₹)</Label>
              <Input
                id="opening_balance"
                type="number"
                value={bankForm.opening_balance}
                onChange={(e) => setBankForm({ ...bankForm, opening_balance: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                data-testid="opening-balance-input"
              />
              <p className="text-xs text-gray-500 mt-1">Initial balance when adding this account</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveBank}
              disabled={savingBank}
              data-testid="save-bank-btn"
            >
              {savingBank ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingBank ? 'Update' : 'Add'} Bank
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceSetup;
